import { DateFormat, formatDateTimeWithLocale } from '@frontend/common';
import { IoTClient } from '@frontend/iot/api';
import { IoT, IoTType } from '@frontend/iot/types';
import { LabelClient, LabelTemplateClient } from '@frontend/label/api';
import { Label, LabelTemplate } from '@frontend/label/types';
import { CommonMessage, useLocale } from '@frontend/lang';
import { SettingKey } from '@frontend/setting/types';
import { TransactionExtendedClient } from '@frontend/transaction/api';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface TransactionDetail {
    key: string;
    label: ReactNode;
    value: string;
}

interface PrintLabelsViewProps {
    canPrint: boolean;
    isLoading: boolean;
    printer: IoT | null;
    availablePrinters: IoT[];
    transaction: ReturnType<typeof useWizardTransaction>['transaction'];
    showTransactionDetails: boolean;
    visibleTransactionDetails: TransactionDetail[];
    onToggleTransactionDetails: () => void;
    onSelectPrinter: (iot: IoT) => void;
    setQuantity: (quantity: number) => void;
    onPrint: () => void;
    printing: boolean;
    quantity: number;
    onBack: ReturnType<typeof useScreen>['onBack'];
    onSubmit: ReturnType<typeof useScreen>['onSubmit'];
}

const hasDisplayValue = (value?: string | null): value is string => !!value && value.trim().length > 0;

const usePrintLabels = (): PrintLabelsViewProps => {
    const { store, onBack, updateWizardData, onSubmit } = useScreen();
    const { locale } = useLocale();
    const { transaction, localUpdateTransaction } = useWizardTransaction({
        wizardId: store!.wizard.id,
        store
    });
    const { package: _package } = useWizardPackage({
        wizardId: store!.wizard.id,
        store
    });

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [printer, setPrinter] = useState<IoT | null>(store?.data.printer || null);
    const [availablePrinters, setAvailablePrinters] = useState<IoT[]>([]);
    const [labelTemplate, setLabelTemplate] = useState<LabelTemplate | null>(store?.data.labelTemplate || null);
    const [labels, setLabels] = useState<Label[]>(Array.isArray(store?.data.labels) ? (store?.data.labels as Label[]) : []);
    const [printing, setPrinting] = useState<boolean>(false);
    const [quantity, setQuantity] = useState<number>(1);
    const [showTransactionDetails, setShowTransactionDetails] = useState<boolean>(false);
    const initializedWizardIdRef = useRef<string | null>(null);
    const labelsFetchedForTransactionRef = useRef<string | null>(null);
    const transactionFetchedForPackageRef = useRef<string | null>(null);
    const printRequestInFlightRef = useRef<boolean>(false);

    useEffect(() => {
        if (!store) {
            setIsLoading(false);
            return;
        }

        if (initializedWizardIdRef.current === store.wizard.id) {
            return;
        }
        initializedWizardIdRef.current = store.wizard.id;

        const cachedPrinter = (store.data.printer as IoT | undefined) || null;
        const cachedTemplate = (store.data.labelTemplate as LabelTemplate | undefined) || null;

        if (cachedPrinter) {
            setPrinter(cachedPrinter);
        }
        if (cachedTemplate) {
            setLabelTemplate(cachedTemplate);
        }

        const labelTemplateId = store.functionalitySettings.find((s) => s.key === SettingKey.LABEL_TEMPLATE_ID)?.value;
        if (!cachedTemplate && labelTemplateId && typeof labelTemplateId === 'string') {
            LabelTemplateClient.fetchAccountLabelTemplate(store.wizard.account_id, labelTemplateId).then((template) => {
                setLabelTemplate(template);
                updateWizardData({ labelTemplate: template });
            });
        }

        if (cachedPrinter) {
            setIsLoading(false);
            return;
        }

        setIsLoading(false);
    }, [store, updateWizardData]);

    useEffect(() => {
        const resolveTransactionFromPackage = async () => {
            if (transaction?.id || !_package?.account_id || !_package.transaction_id) return;
            if (transactionFetchedForPackageRef.current === _package.transaction_id) return;

            transactionFetchedForPackageRef.current = _package.transaction_id;

            try {
                const fetchedTransaction = await TransactionExtendedClient.fetchExtendedTransaction(_package.account_id, _package.transaction_id);
                await localUpdateTransaction(fetchedTransaction);
            } catch (error) {
                transactionFetchedForPackageRef.current = null;
                console.error('Error fetching transaction for package', error);
            }
        };

        resolveTransactionFromPackage();
    }, [_package?.account_id, _package?.transaction_id, transaction?.id, localUpdateTransaction]);

    useEffect(() => {
        if (!store?.wizard.account_id) return;
        IoTClient.fetchIoTs({ account_id: store.wizard.account_id, type: IoTType.PRINTER } as any).then((result) => {
            setAvailablePrinters(result.results);
            if (!printer) {
                const defaultPrinterId = store.functionalitySettings.find((s) => s.key === SettingKey.DEFAULT_PRINTER_ID)?.value;
                const defaultPrinter = defaultPrinterId ? result.results.find((p) => p.id === defaultPrinterId) : null;
                const selected = defaultPrinter ?? result.results[0] ?? null;
                setPrinter(selected);
                if (selected) updateWizardData({ printer: selected });
            }
        });
    }, [store?.wizard.account_id]);

    useEffect(() => {
        if (!transaction?.account_id || !transaction.id || labels.length > 0) return;
        if (labelsFetchedForTransactionRef.current === transaction.id) return;

        labelsFetchedForTransactionRef.current = transaction.id;

        LabelClient.fetchLabels({ account_id: transaction.account_id, transaction_id: transaction.id } as any)
            .then((result) => {
                const transactionLabels = result.results.filter((label) => label.transaction_id === transaction.id);
                if (transactionLabels.length > 0) {
                    setLabels(transactionLabels);
                    updateWizardData({
                        labels: transactionLabels,
                        labelPrinted: true
                    });
                }
            })
            .catch(() => {
                // Non-blocking: if lookup fails, printing will create labels as fallback.
            });
    }, [transaction?.account_id, transaction?.id, labels.length, updateWizardData]);

    const onPrint = async () => {
        if (printRequestInFlightRef.current) return;
        if (!printer || !labelTemplate || !transaction || !transaction.id || !transaction.account_id) return;

        printRequestInFlightRef.current = true;
        setPrinting(true);

        try {
            let labelsToPrint = labels;

            if (labelsToPrint.length === 0) {
                const existingLabelResult = await LabelClient.fetchLabels({ account_id: transaction.account_id, transaction_id: transaction.id } as any);
                labelsToPrint = existingLabelResult.results.filter((label) => label.transaction_id === transaction.id);
            }

            if (labelsToPrint.length === 0) {
                const labelResult = await LabelClient.generateTransactionLabel(transaction.account_id, transaction.id, {
                    language: locale,
                    template_id: labelTemplate.id
                });
                labelsToPrint = labelResult.results;
            }

            const uniqueLabelsToPrint = Array.from(new Map(labelsToPrint.map((label) => [label.id, label])).values());
            if (uniqueLabelsToPrint.length === 0) return;

            setLabels(uniqueLabelsToPrint);

            const printPromises = uniqueLabelsToPrint.map((label) =>
                LabelClient.printLabel(transaction.account_id!, label.id, { iot_id: printer.id, quantity })
            );
            await Promise.all(printPromises);

            updateWizardData({
                labels: uniqueLabelsToPrint,
                labelPrinted: true
            });
        } catch (e) {
            console.error('Error printing labels', e);
        } finally {
            printRequestInFlightRef.current = false;
            setPrinting(false);
        }
    };

    const transactionDetails = useMemo<TransactionDetail[]>(() => {
        if (!transaction) return [];

        return [
            {
                key: 'tracking_number',
                label: CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER,
                value: transaction.tracking_number ?? ''
            },
            {
                key: 'supplier',
                label: CommonMessage.OBJECTS.SUPPLIER.DEFAULT,
                value: transaction.supplier ?? ''
            },
            {
                key: 'purchase_order',
                label: CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER,
                value: transaction.purchase_order ?? ''
            },
            {
                key: 'shipping_notes',
                label: CommonMessage.OBJECTS.TRANSACTION.SHIPPING_NOTES,
                value: transaction.shipping_notes ?? ''
            },
            {
                key: 'creation_timestamp',
                label: CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT,
                value: transaction.creation_timestamp
                    ? formatDateTimeWithLocale(new Date(transaction.creation_timestamp), locale, DateFormat.DD_MM_YYYY_HH_MM)
                    : ''
            }
        ].filter((detail) => hasDisplayValue(detail.value));
    }, [locale, transaction]);

    const visibleTransactionDetails = showTransactionDetails ? transactionDetails : transactionDetails.filter((detail) => detail.key === 'tracking_number');

    const onToggleTransactionDetails = () => {
        setShowTransactionDetails((current) => !current);
    };

    const onSelectPrinter = (iot: IoT) => {
        setPrinter(iot);
        updateWizardData({ printer: iot });
    };

    return {
        canPrint: !!(printer && labelTemplate && transaction && transaction.id && transaction.account_id),
        isLoading,
        printing,
        printer,
        availablePrinters,
        quantity,
        transaction,
        showTransactionDetails,
        visibleTransactionDetails,
        onToggleTransactionDetails,
        onPrint,
        onSelectPrinter,
        setQuantity,
        onSubmit,
        onBack
    };
};

export default usePrintLabels;
